import { Component, OnInit, Input, inject } from '@angular/core';
import { NgForm, UntypedFormGroup, FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { uniq, isEmpty, isEqual } from 'lodash-es';
import { Client } from '../../../core/models/client.model';
import { Upload } from '../../../core/models/upload.model';
import { usStates } from '../../../core/options/states.opts';
import { ClientsService } from '../../../core/services/clients.service';
import { UploadService } from '../../../core/services/upload.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { UsersService } from '../../../core/services/users.service';
import { EmrSystemsService } from '../../../core/services/emr-systems.service';
import { UserType } from '../../../core/enums/user-type.enum';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { UserRoleName } from '../../../core/enums/user-role-name.enum';
import { ThirdPartyAdministersService } from 'app/core/services/third-party-administers.service';
import { DateUtils } from 'app/core/lib/date-utils';
import { isNotBlank } from 'app/core/lib/js-utils';
import { NgClass, KeyValuePipe } from '@angular/common';
import { FormControlServerErrorsComponent } from '../../../shared/components/form-control-server-errors/form-control-server-errors.component';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';
import { EmrSystem } from 'app/core/models/emr-system.model';

export enum ClientFormMode {
  create,
  update,
}

const ehrInstanceValueBlankTag = 'blank';
const ehrInstanceValueBlankPlaceholder = '(blank)';

@Component({
    selector: 'app-client-form',
    templateUrl: './client-form.component.html',
    styleUrls: ['./client-form.component.scss'],
    imports: [FormsModule,
              NgClass,
              FormControlServerErrorsComponent,
              NgSelectComponent,
              ClientSelectComponent,
              NgOptionComponent,
              KeyValuePipe]
})
export class ClientFormComponent implements OnInit {
  private clientsService = inject(ClientsService);
  private uploadService = inject(UploadService);
  private navigationService = inject(NavigationService);
  private usersService = inject(UsersService);
  private emrSystemsService = inject(EmrSystemsService);
  private authService = inject(AuthenticationService);
  private thirdPartyAdministersService = inject(ThirdPartyAdministersService);

  @Input() client: Client = null;
  @Input() mode: ClientFormMode = null;

  clientForm = null;
  saving = false;
  states = usStates;
  logoFile: File = null;
  logoFileErrors: string[] = [];
  deleteLogo = false;
  users = [];
  thirdPartyAdministers = [];
  entityTypes = [];
  savedHrsaIdStrings = [];
  allowedClientFileEhrInstanceValuesForDisplay = [];
  disableClaimIdentifierPrefixInput: boolean;
  healthGorillaDefaults = {
    enabled: false,
    identifier: '',
    obfuscatedSecret: '',
    secret: '',
    url: '',
    jwtSub: '',
    scopes: '',
  };
  healthGorillaErrors = {
    identifier: false,
    secret: false,
    url: false,
    jwtSub: false,
    scopes: false,
  };
  emrSystems: EmrSystem[] = [];

  ngOnInit() {
    this.translateAllowedClientFileEhrInstanceValuesForDisplay();
    this.loadUsers();
    this.loadThirdPartyAdministers();
    this.loadClientEntityTypes();
    this.loadEmrSystems();
    this.disableClaimIdentifierPrefixInput = isNotBlank(this.client.claimIdentifierPrefix);
    this.savedHrsaIdStrings = this.client.hrsaIdStrings || [];

    if (!this.client.healthGorillaSettings) {
      this.client.healthGorillaSettings = {...this.healthGorillaDefaults}
    }
  }

  onFormSubmit(form: NgForm) {
    const clientForm = form.form;

    const healthGorillaIsValid = this.validateHealthGorillaSettings();

    if (clientForm.valid && healthGorillaIsValid) {
      this.saving = true;
      this.logoFileErrors = [];

      const saveFn = this.saveClientAndUploadFiles(clientForm);
      saveFn.subscribe(
        () => this.navigateAwayAfterComplete(),
        response => this.handleSaveError(response, clientForm)
      );
    }
  }

  getSecretPlaceholder(): string {
    if (this.client.healthGorillaSettings.obfuscatedSecret.length === 0) {
      return ""
    } else {
      return 'New secret (leave blank to keep current)';
    }
  }

  private saveClientAndUploadFiles(clientForm: UntypedFormGroup): Observable<Client> {
    return this.saveClient(clientForm).pipe(
      mergeMap(() => this.handleLogoFile())
    );
  }

  private handleLogoFile(): Observable<any> {
    if (this.logoFile) {
      return this.uploadLogo().pipe(
        mergeMap(
          upload => this.associateLogoFileWithClient(upload)
        ),
        catchError((error) => {
          this.saving = false;
          this.logoFileErrors = error.error.errors;
          return EMPTY;
        })
      );
    } else {
      return of(this.client);
    }
  }

  private updateClient(client: Client) {
    this.client = client;
    return client;
  }

  private uploadLogo(): Observable<Upload> {
    return this.uploadService.upload(this.logoFile);
  }

  private associateLogoFileWithClient(upload: Upload): Observable<Client> {
    return this.clientsService.uploadLogo(this.client.id, upload.signedId);
  }

  private handleSaveError(response: HttpErrorResponse, clientForm: UntypedFormGroup): void {
    this.saving = false;

    if (response.status === 400) {
      const { error: data } = response;

      Object.keys(data.errors).forEach(fieldName => {
        clientForm.get(fieldName).setErrors({ server: data.errors[fieldName] });
      });
    } else {
      console.error(response);
    }
  }

  private saveClient(clientForm: UntypedFormGroup): Observable<Client> {
    const logoAttributes = this.deleteLogo ? { logo: { _destroy: true } } : {};
    let attributes = Object.assign(clientForm.value, logoAttributes);

    attributes.allowedClientFileEhrInstanceValues = this.translateAllowedClientFileEhrInstanceValuesForSave();

    if (this.shouldSaveHealthGorilla()) {
      const scrubbed = { ...this.client.healthGorillaSettings};
      delete scrubbed.obfuscatedSecret;
      attributes.healthGorillaSettingsAttributes = scrubbed;
    }

    if (attributes.assignedUsers) {
      // We only want to send down an array of username and not user objects
      attributes.assignedUsers = attributes.assignedUsers.map(u => u.username);
    }

    if (attributes.emrSystems) {
      const { emrSystems, ...rest } = attributes;
      attributes = { ...rest, ...this.buildEmrSystemsParams(emrSystems) };
    }

    const serviceFn: Observable<Client> =
      this.mode === ClientFormMode.create
        ? this.clientsService.create(attributes)
        : this.clientsService.update(this.client.id, attributes);

    return serviceFn.pipe(map(client => this.updateClient(client)));
  }

  private buildEmrSystemsParams(selectedEmrSystems: (string | EmrSystem)[] ) {
    const emrSystemIds = [];
    const emrSystemNewNames = [];

    const usedIds = new Set();
    const usedNames = new Set();

    (selectedEmrSystems || []).forEach(item => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        if (item.id && !usedIds.has(item.id)) {
          emrSystemIds.push(item.id);
          usedIds.add(item.id);

          if (item.name) {
            usedNames.add(item.name.trim().toLowerCase());
          }
        }
      }
    });

    (selectedEmrSystems || []).forEach(item => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        const key = trimmed.toLowerCase();

        if (trimmed.length > 0 && !usedNames.has(key)) {
          emrSystemNewNames.push(trimmed)
          usedNames.add(key);
        }
      }
    });

    return { emrSystemIds, emrSystemNewNames }
  }

  private shouldSaveHealthGorilla() {
    return(!!this.client.healthGorillaSettings.id ||
      !isEqual(this.client.healthGorillaSettings, this.healthGorillaDefaults))
  }

  private navigateAwayAfterComplete() {
    if (this.client.id) {
      this.navigationService.navigateTo(`/capture-admin/client-management/clients/${this.client.id}`);
    } else {
      this.navigationService.navigateTo(`/capture-admin/client-management/clients`);
    }
  }

  private isControlInvalid(form: NgForm, controlName: string) {
    const control = form.form.get(controlName);

    if (control) {
      return control.invalid && form.submitted;
    }
  }

  onClaimTypeClick($event, clientForm) {
    clientForm.form.get('applicableToReferralClaims').setErrors(null);
    clientForm.form.get('applicableToIccClaims').setErrors(null);
  }

  invalidCss(form: NgForm, controlName: string) {
    return {
      'is-invalid': this.isControlInvalid(form, controlName),
    };
  }

  onLogoFileChange(event): void {
    const fileInput = event.target;

    if (fileInput.files && fileInput.files.length > 0) {
      this.logoFile = fileInput.files[0];
    }
  }

  addHrsaId(id: string) {
    return id;
  }

  onNgSelectBlur(select) {
    if (select.showAddTag) {
      select.selectTag();
    }
  }

  addAlternateWagBillingIdentifier(value: string) {
    return value;
  }

  addEhrInstance(value: string) {
    return value;
  }

  addClientFileMonitoringEmailRecipient(id: string) {
    return id;
  }

  addEmrSystemName(value: string) {
    return value;
  }

  onDeleteLogo($event) {
    $event.preventDefault();

    delete this.client.logoUrl;
    this.deleteLogo = true;
  }

  onCancelClick() {
    this.navigateAwayAfterComplete();
  }

  onDateDealClosedBlur(form: NgForm) {
    const dateDealClosed = form.form.get('dateDealClosed');

    if (!this.isValidDate(this.client.dateDealClosed)) {
      dateDealClosed.setValue(null);
    }
  }

  onGoLiveDateBlur(form: NgForm) {
    const goLiveDate = form.form.get('goLiveDate');

    if (!this.isValidDate(this.client.goLiveDate)) {
      goLiveDate.setValue(null);
    }
  }

  onEspSubmitStartDateBlur(form: NgForm) {
    const espSubmitStartDate = form.form.get('espSubmitStartDate');

    if (!this.isValidDate(this.client.espSubmitStartDate)) {
      espSubmitStartDate.setValue(null);
    }
  }

  onAllowedClientFileEhrInstanceValuesChange(values) {
    this.allowedClientFileEhrInstanceValuesForDisplay = uniq(
      values.map(this.translateAllowedClientFileEhrInstanceValueForDisplay)
    );
  }

  private translateAllowedClientFileEhrInstanceValueForDisplay(value) {
    if (
      !value ||
        (typeof value === 'string' &&
          (value.toLowerCase() === ehrInstanceValueBlankTag || value === ehrInstanceValueBlankPlaceholder))
    ) {
      return ehrInstanceValueBlankPlaceholder;
    } else {
      return value.toLowerCase();
    }
  }

  private translateAllowedClientFileEhrInstanceValuesForDisplay() {
    this.allowedClientFileEhrInstanceValuesForDisplay = uniq(
      this.client.effectiveAllowedClientFileEhrInstanceValues.map(
        this.translateAllowedClientFileEhrInstanceValueForDisplay
      )
    );
  }

  private translateAllowedClientFileEhrInstanceValueForSave(value) {
    if (value === ehrInstanceValueBlankPlaceholder) {
      return '';
    } else {
      return value;
    }
  }

  private translateAllowedClientFileEhrInstanceValuesForSave() {
    return this.allowedClientFileEhrInstanceValuesForDisplay.map(
      this.translateAllowedClientFileEhrInstanceValueForSave
    );
  }

  private loadUsers() {
    if (this.canManageUsers) {
      this.usersService.getByUserType([UserType.captureAdmin]).subscribe(result => {
        this.users = result.users;
      });
    }
  }

  private loadThirdPartyAdministers() {
    this.thirdPartyAdministersService.getList().subscribe(result => {
      this.thirdPartyAdministers = result.thirdPartyAdministers;
    });
  }

  private loadClientEntityTypes() {
    this.clientsService.getEntityTypes().subscribe(result => {
      this.entityTypes = result;
    });
  }

  private loadEmrSystems() {
    this.emrSystemsService.getList().subscribe(result => {
      this.emrSystems = result
    });
  }

  get fileProcessingDisabled() {
    return this.client.fileProcessingEnabled === false;
  }

  get canManageImplementation() {
    return this.authService.hasPermissionTo(UserRoleName.manageImplementation);
  }

  get canManageUsers() {
    return this.authService.isCaptureAdminUser && this.authService.hasPermissionTo(UserRoleName.manageUsers);
  }

  private isValidDate(date: string) {
    return DateUtils.isValid(date);
  }

  get hasLogoFileErrors() {
    return !isEmpty(this.logoFileErrors);
  }

  private validateHealthGorillaSettings(): boolean {
    const hg = this.client.healthGorillaSettings;
    // note that this doesn't support clearing out all of the fields in order
    // to delete the ClientHealthGorillaSettings record
    const shouldValidate =
      hg.enabled ||
        isNotBlank(hg.identifier) ||
        (hg.obfuscatedSecret.length === 0 && isNotBlank(hg.secret)) ||
        isNotBlank(hg.url) ||
        isNotBlank(hg.jwtSub) ||
        isNotBlank(hg.scopes);

    if (!shouldValidate) {
      this.healthGorillaErrors = {
        identifier: false,
        secret: false,
        url: false,
        jwtSub: false,
        scopes: false,
      };
      return true;
    }

    this.healthGorillaErrors.identifier = !isNotBlank(hg.identifier);
    if (hg.obfuscatedSecret.length === 0) {
      this.healthGorillaErrors.secret = !isNotBlank(hg.secret);
    }
    this.healthGorillaErrors.url = !isNotBlank(hg.url);
    this.healthGorillaErrors.jwtSub = !isNotBlank(hg.jwtSub);
    this.healthGorillaErrors.scopes = !isNotBlank(hg.scopes);

    const hasErrors = Object.values(this.healthGorillaErrors).some(Boolean);
    return !hasErrors;
  }

}
